import type { VerifyWhiteList } from './declare'
import React, { useState, useEffect, useContext } from 'react'
import { reaction } from 'mobx'
import { useLocalObservable } from 'mobx-react'
import { Spin } from 'antd'
import { ListPropsContext } from 'src/components'
import { EditPageHeader, EditPageColumns, EditPageTable, EditPageBottom } from './components'
import { appStore, listStore, dynamicJsStore } from 'src/stores'
import { api, $api, $consts, $evt } from 'src/plugins'
import { useLatest, useIsLoading } from 'src/hooks'
import { useModalContext } from 'src/components/common/EasyModal'
import {
  getValidNavColumns,
  controlFieldVerify,
  getNavFieldsFromNavField,
  getNavFieldsFromNavColumns,
  getEditSubPageNavFields,
  getDefaultValueFrom_DEFAULT_VALUE,
  getModalWidthFromColNum
} from './helper'
import { initFilter, getSingleRule, extendRules, extendInclude } from 'src/utils/common'
import { guid } from 'src/utils/tools'
import { set, get } from 'lodash'
import 'src/styles/components/root/editPage.less'

type FormData = {
  // 预设的 formBase 数据
  initFormBase?: any,
  // 表单本身配置的 formId
  formId?: string,
  // 当前表单父表单配置的 formId（比如说树表单，组合表单）
  parentFormId?: string,
  // 编辑表单的全局禁用
  formDisabled?: boolean,
  // 列表表单操作按钮的禁用（需求是列表表单移动到编辑表单头部 -- 已于 23.03.29 暂停）
  operateDisabled?: boolean,
  // 是否批量编辑表单
  batchEdit?: boolean,
  // 是否复制编辑表单
  copyEdit?: boolean
}

type Props = {
  formCode: string,
  formData?: FormData,
}

const EditPropsContext = React.createContext<Props>({ formCode: '' })
const EditContext = React.createContext<any>(undefined)
const EditPage = (props: Props) => {
  const listProps = useContext(ListPropsContext)
  const table = listStore.table.get(listProps.formCode)

  const latestProps = useLatest(props)

  const modal = useModalContext()

  const state = useLocalObservable(() => {
    return {
      // EditPageTable 的实例
      editPageTable: {} as any,
      setEditPageTable (editTable: any) {
        this.editPageTable = editTable
      },
      // 编辑表单配置
      editConfig: {} as any,
      setEditConfig (config: any) {
        this.editConfig = config
      },
      // 表单有效 Nav_Columns 配置
      get validNavColumns () {
        const { Nav_PageEdit = {} } = this.editConfig
        const { Nav_Columns = [] } = Nav_PageEdit
        const filterFields = ['IS_DEFAULT']
        props.formData?.batchEdit && filterFields.push('IS_CAN_BATCH_EDIT')
        return getValidNavColumns(Nav_Columns, filterFields)
      },
      // 表单动态加载 js
      get dynamicJs () {
        const { Nav_Form } = this.editConfig
        const { JS_FILES } = Nav_Form
        const funcs = dynamicJsStore.dynamicJs[JS_FILES?.split('.')[0]]
        return funcs || {}
      },
      // 编辑表单 form 表单数据
      formBase: {},
      setFormBase (data: any) {
        Object.assign(this.formBase, data)
        /**
         * 校验字段，
         * 放在微任务中是因为很有可能 setFormBase 之后，会有事件来更新白名单，
         * 所以放在更新的白名单之后进行字段校验
         */
        Promise.resolve().then(() => {
          const verify: any = {}
          for (const [FIELD_NAME, value] of Object.entries(data)) {
            const find = this.validNavColumns.find((item: any) => item.FIELD_NAME === FIELD_NAME)
            if (find && !this.verifyWhiteList.map(item => item.field).includes(FIELD_NAME)) {
              verify[FIELD_NAME] = controlFieldVerify(find, value)
            }
          }
          this.setVerify(verify)
        })
      },
      // 批量编辑 form 表单数据
      formBatch: {},
      setFormBatch (data: any) {
        Object.assign(this.formBatch, data)
      },
      // 批量编辑选中行数据
      batchData: [] as any[],
      setBatchData (data: any[]) {
        this.batchData = data
      },
      // 编辑表单字段检验白名单
      verifyWhiteList: [] as VerifyWhiteList,
      setVerifyWhiteList (list: VerifyWhiteList) {
        this.verifyWhiteList = list
      },
      // 编辑表单字段检验信息，{ Name: { invalid: true, msg: '必填项' } }
      verify: {},
      setVerify (data: any) {
        Object.assign(this.verify, data)
      },
      // 是否存在校验失败项
      checkVerifyIsInvalid () {
        const verify: any = {}
        this.validNavColumns.forEach((item: any) => {
          if (this.verifyWhiteList.map(list => list.field).includes(item.FIELD_NAME)) return

          verify[item.FIELD_NAME] = controlFieldVerify(item, get(this.formBase, item.FIELD_NAME))
        })
        this.setVerify(verify)
        const isInvalidEditPageColumns = Object.entries(verify).map(([key, value]) => value).filter((item: any) => !!item.invalid).length
        const isInvalidEditPageTable = this.editPageTable.checkVerifyIsInvalid?.()
        return isInvalidEditPageColumns || isInvalidEditPageTable
      },
    }
  })

  // 编辑表单配置
  useEffect(() => {
    const { OrgId } = appStore.current.headers
    const payload = initFilter({ OrgId, Keyword: props.formCode })
    $api['edit/getEditPageConfigInfo'](payload).then((res: any) => {
      if (res) {
        // 查找所有的枚举
        const { Nav_SubPageEdits = [] } = res
        const allEnums: string[] = []
        Nav_SubPageEdits.forEach((table: any) => {
          allEnums.push(...table.Nav_Columns.map((item: any) => item.ENUM).filter((item: any) => item))
        })
        // 存在枚举，则将枚举值赋值在表格配置中
        if (allEnums.length) {
          return appStore.getEnums(allEnums).then((data: any[]) => {
            Nav_SubPageEdits.forEach((table: any) => {
              set(table, 'Nav_Enums', data)
            })
            return res
          })
        }
      }
      return res
    }).then((res: any) => {
      if (res) {
        state.setEditConfig(res)
        // 动态更改 Modal 的标题和宽度
        const cols = res.Nav_PageEdit?.Nav_PageEdit?.COL_NUM
        modal.updateModal?.({
          title: res.Nav_Form?.NAME,
          width: `${getModalWidthFromColNum(cols)}%`
        })
      }
      state.dynamicJs[$consts['LIFECYCLE/EDIT_PAGE_CONFIG_LOADED']]?.(state, latestProps, { listProps, table })
      $evt.emit($consts['LIFECYCLE/EDIT_PAGE_CONFIG_LOADED'], {
        state,
        latestProps,
        formCode: props.formCode,
        data: res
      })
    })
  }, [props.formCode])

  // 编辑表单默认值
  useEffect(() => reaction(
    () => state.editConfig.Nav_PageEdit?.Nav_Columns?.length,
    () => {
      const { OrgId: ORG_ID } = appStore.current.headers
      const { Nav_PageEdit = {} } = state.editConfig
      const { Nav_PageEdit: deepConfig, Nav_Columns } = Nav_PageEdit
      const presetForm: any = {
        ID: guid(),
        ORG_ID,
        FORM_ID: deepConfig.PAGE_FORM_ID,
        ENTITY_ORG_TPYE: deepConfig.EDIT_PAGE_ENTITY_ORG_TYPE || 0,
        PARENT_ID: props.formData?.parentFormId,
        ...props.formData?.initFormBase
      }
      // 此处不使用 validNavColumns 是因为已配置但是不显示的字段也要传值
      Nav_Columns.forEach((item: any) => {
        if (item.DEFAULT_VALUE !== undefined) {
          const defaultValue = getDefaultValueFrom_DEFAULT_VALUE(item.DEFAULT_VALUE, { user: appStore.current.userinfo })
          presetForm[item.FIELD_NAME] = defaultValue
        }
      })
      state.setFormBase(presetForm)
    }
  ), [props.formData?.parentFormId, props.formData?.initFormBase])

  // 编辑表单详情
  useEffect(() => reaction(
    () => {
      const { Nav_PageEdit = {}, Nav_SubPageEdits = [] } = state.editConfig
      const { Nav_PageEdit: deepConfig, Nav_Columns } = Nav_PageEdit
      if (props.formData?.formId && deepConfig?.QUERY_API_URL) {
        const { OrgId } = appStore.current.headers
        const payload = initFilter({ OrgId })
        extendRules(payload, [getSingleRule('ID', 1, props.formData.formId)])
        // 编辑表单导航属性
        const pageNavFields = getNavFieldsFromNavColumns(Nav_Columns)
        // 编辑表单子表导航属性
        const subPageNavFields = getEditSubPageNavFields(Nav_SubPageEdits)
        const navFields = pageNavFields.concat(subPageNavFields)
        navFields.length && extendInclude(payload, navFields)
        return { payload: payload, url: deepConfig.QUERY_API_URL }
      }
      return {}
    },
    ({ payload, url }: any) => {
      url && $api['app/getDataByPost'](payload, { url }).then((res: any) => {
        res && state.setFormBase(res)
        state.dynamicJs[$consts['LIFECYCLE/EDIT_PAGE_FORM_LOADED']]?.(state, latestProps, { listProps, table })
        $evt.emit($consts['LIFECYCLE/EDIT_PAGE_FORM_LOADED'], {
          state,
          latestProps,
          formCode: props.formCode,
          data: res
        })
      })
    }
  ), [props.formData?.formId])

  // 根据父表单填充编辑表单详情
  useEffect(() => reaction(
    () => {
      const { Nav_PageEdit = {}, Nav_SubPageEdits = [] } = state.editConfig
      const { Nav_PageEdit: deepConfig, Nav_Columns } = Nav_PageEdit
      if (props.formData?.parentFormId && deepConfig?.QUERY_API_URL) {
        const childFields = deepConfig?.CHILD_SET_FIELD?.split(',')
        const parentFields = deepConfig?.PARENT_COPY_FIELD?.split(',')
        if (childFields?.length && parentFields?.length && childFields.length === parentFields.length) {
          const { OrgId } = appStore.current.headers
          const payload = initFilter({ OrgId })
          extendRules(payload, [getSingleRule('ID', 1, props.formData.parentFormId)])
          // 编辑表单导航属性
          const pageNavFields = getNavFieldsFromNavColumns(Nav_Columns)
          // 编辑表单子表导航属性
          const subPageNavFields = getEditSubPageNavFields(Nav_SubPageEdits)
          // 表单配置导航属性
          const childNavFields = getNavFieldsFromNavField(deepConfig.CHILD_SET_FIELD)
          const parentNavFields = getNavFieldsFromNavField(deepConfig.PARENT_COPY_FIELD)
          const navFields = pageNavFields.concat(subPageNavFields, childNavFields, parentNavFields)
          navFields.length && extendInclude(payload, navFields)
          return {
            payload: payload,
            url: deepConfig.QUERY_API_URL,
            childFields,
            parentFields
          }
        }
      }
      return {}
    },
    ({ payload, url, childFields, parentFields }: any) => {
      url && $api['app/getDataByPost'](payload, { url }).then((res: any) => {
        if (res) {
          const base = {}
          childFields.forEach((field: string, index: number) => {
            set(base, field, get(res, parentFields[index]))
          })
          state.setFormBase(base)
        }
        state.dynamicJs[$consts['LIFECYCLE/EDIT_PAGE_FORM_LOADED']]?.(state, latestProps, { listProps, table })
        $evt.emit($consts['LIFECYCLE/EDIT_PAGE_FORM_LOADED'], {
          state,
          latestProps,
          formCode: props.formCode,
          data: res
        })
      })
    }
  ), [props.formData?.parentFormId])

  // 批量编辑表单数据
  useEffect(() => reaction(
    () => {
      const { Nav_PageEdit = {} } = state.editConfig
      const { Nav_PageEdit: deepConfig } = Nav_PageEdit
      const selectedKeys = table?.selectedKeys
      if (props.formData?.batchEdit && deepConfig?.BATCH_QUERY_API && selectedKeys?.length) {
        const { OrgId } = appStore.current.headers
        const payload = initFilter({ OrgId })
        set(payload, 'FilterGroup.IsAnd', false)
        // 列表表单行选中数据
        const rules = selectedKeys.map((key: string) => getSingleRule('ID', 1, key))
        extendRules(payload, rules)
        // 编辑表单导航属性
        const pageNavFields = getNavFieldsFromNavColumns(state.validNavColumns)
        extendInclude(payload, pageNavFields)
        return { payload: payload, url: deepConfig.BATCH_QUERY_API }
      }
      return {}
    },
    ({ payload, url }: any) => {
      url && $api['app/getDataByPost'](payload, { url }).then((res: any) => {
        // 批量编辑表单行选中数据
        res && state.setBatchData(res)
      })
    }
  ), [props.formData?.batchEdit])

  // loading
  const [paths, setPaths] = useState([api.paths['edit/getEditPageConfigInfo']])
  useEffect(() => reaction(
    () => state.editConfig.Nav_PageEdit?.Nav_PageEdit?.QUERY_API_URL,
    path => setPaths(prev => prev.concat(path))
  ), [])
  const loading = useIsLoading(paths)
  return (
    <EditPropsContext.Provider value={props}>
      <EditContext.Provider value={state}>
        <Spin delay={200} spinning={loading}>
          <div className='edit-page'>
            <EditPageHeader />
            <EditPageColumns />
            {!props.formData?.batchEdit ? <EditPageTable /> : null}
            <EditPageBottom />
          </div>
        </Spin>
      </EditContext.Provider>
    </EditPropsContext.Provider>
  )
}

export { EditPropsContext, EditContext }
export default EditPage